import React, { useState, useEffect } from 'react';
import {
    Container,
    Drawer,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Box,
    Card,
    CardContent,
    Tabs,
    Tab,
    Badge,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import { Paper } from '@mui/material';

import Statistics from '../components/Statistics';
import ActivePlansAndHistory from "../components/ActivePlansAndHistory";
import ProfileEdit from '../components/ProfileEdit';
import ChangePassword from '../components/ChangePassword';
import ProfileView from '../components/ProfileView';
import CreditView from '../components/CreditView';
import UserContracts from '../components/UserContracts';
import Transactions from "../components/Transactions";
import VisitHistory from "../components/VisitHistory";
import TrainingPlans from '../components/TrainingPlans';
import FamilyMembers from '../components/FamilyMembers';
import Calendar from '../components/CalendarSettings';
import CapUploadPanel from "../components/CapUploadPanel";

import { getUserProfile, updateUserProfile, changeUserPassword } from '../api/profileApi';
import { uploadProfilePicture } from '../api/logoApi';
import { getUserContracts } from '../api/contractApi';

const baseMenuItems = [
    { id: 'my-profile', label: 'My Profile', component: ProfileView },
    { id: 'active-plans', label: 'Active Memberships', component: ActivePlansAndHistory },
    { id: 'visit-history', label: 'Visit History', component: VisitHistory },
    { id: 'training-plans', label: 'Training Plans', component: TrainingPlans },
    { id: 'family-members', label: 'Children', component: FamilyMembers },
    { id: 'transactions', label: 'Transactions', component: Transactions },
    { id: 'credit', label: 'Credit & Gift Cards', component: CreditView },
    { id: 'user-contracts', label: 'Contracts', component: UserContracts },
    { id: 'statistics', label: 'Statistics', component: Statistics },
    { id: 'calendar-settings', label: 'Calendar Settings', component: Calendar },
];

export default function MyProfile() {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeComponent, setActiveComponent] = useState('my-profile');
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Kasutame punase märgendina kuvatavat arvu, kui status = 'sent'
    const [sentCount, setSentCount] = useState(0);

    // Teavituste state'id
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' // 'success', 'error', 'warning', 'info'
    });

    const [dialog, setDialog] = useState({
        open: false,
        title: '',
        message: '',
        type: 'success' // 'success' või 'error'
    });

    const [isUploading, setIsUploading] = useState(false);
    const [isSavingClassListVisibility, setIsSavingClassListVisibility] = useState(false);

    const menuItems = [
        ...baseMenuItems,
        ...(user?.id === 6 ? [{ id: 'cap-upload', label: 'CAP Upload', component: CapUploadPanel }] : [])
    ];

    useEffect(() => {
        getUserProfile()
            .then((data) => {
                setUser(data);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching user profile:', error);
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        // Handle calendar callback parameters
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        const calendarParam = urlParams.get('calendar');

        // If redirected from calendar auth, switch to calendar settings tab
        if (tabParam === 'calendar-settings') {
            setActiveComponent('calendar-settings');

            // Show appropriate message based on calendar parameter
            if (calendarParam === 'connected') {
                showSnackbar('Google Calendar connected successfully!', 'success');
            } else if (calendarParam === 'error') {
                showSnackbar('Failed to connect Google Calendar. Please try again.', 'error');
            }

            // Clean URL after handling parameters
            setTimeout(() => {
                window.history.replaceState({}, document.title, window.location.pathname);
            }, 100);
        }
    }, []);


    // Kui kasutaja info on laetud, toome tema lepingud (GET /contracts/user/:userId)
    useEffect(() => {
        if (user?.id) {
            loadUserContracts(user.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadUserContracts = async (userId) => {
        try {
            const data = await getUserContracts(userId);
            if (Array.isArray(data)) {
                const sent = data.filter((c) => c.status === 'Waiting for acceptance').length;
                setSentCount(sent);
            }
        } catch (err) {
            console.error('Error loading user contracts:', err);
        }
    };

    const handleMenuClick = (componentId) => {
        setActiveComponent(componentId);
        setDrawerOpen(false);
    };

    // New handler functions for Edit Profile and Change Password
    const handleEditProfile = () => {
        setActiveComponent('edit-profile-temp');
    };

    const handleChangePassword = () => {
        setActiveComponent('change-password-temp');
    };

    // Teavituste funktsioonid
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const showDialog = (title, message, type = 'success') => {
        setDialog({
            open: true,
            title,
            message,
            type
        });
    };

    const closeSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const closeDialog = () => {
        setDialog(prev => ({ ...prev, open: false }));
    };

    // Täiendatud handleUploadProfilePicture funktsioon
    const handleUploadProfilePicture = async (file) => {
        // Frontend kontrollid
        const maxSize = 15 * 1024 * 1024; // 15MB

        if (file.size > maxSize) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            showDialog(
                'File Too Large',
                `File size is too large! Maximum allowed size is 15MB. Your file is ${fileSizeMB}MB.`,
                'error'
            );
            return;
        }

        // Kontrollige faili tüüpi
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            showDialog(
                'Invalid File Type',
                'Please upload an image file (JPEG, PNG, GIF, or WebP).',
                'error'
            );
            return;
        }

        setIsUploading(true);

        try {
            const updatedUser = await uploadProfilePicture(file, user.id);
            setUser(updatedUser);
            showSnackbar('Profile picture uploaded successfully!', 'success');
        } catch (error) {
            console.error('Upload error:', error);
            showDialog(
                'Upload Failed',
                error.message || 'Failed to upload profile picture. Please try again.',
                'error'
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleSaveProfile = async (updatedUser) => {
        const success = await updateUserProfile(updatedUser);
        if (success) {
            setUser(updatedUser);
            setActiveComponent('my-profile');
            showSnackbar('Profile updated successfully!', 'success');
        } else {
            showDialog('Update Failed', 'Failed to update profile', 'error');
        }
    };

    const handleClassListVisibilityChange = async (isVisible) => {
        const updatedUser = {...user, isClassListNameVisible: isVisible};
        setIsSavingClassListVisibility(true);

        try {
            const success = await updateUserProfile(updatedUser);
            if (success) {
                setUser(updatedUser);
                showSnackbar('Participant list name visibility updated.', 'success');
            } else {
                showDialog('Update Failed', 'Failed to update participant list visibility.', 'error');
            }
        } finally {
            setIsSavingClassListVisibility(false);
        }
    };

    const handleChangePasswordSubmit = async (passwordData) => {
        const success = await changeUserPassword(passwordData);
        if (success) {
            showSnackbar('Password changed successfully!', 'success');
            setActiveComponent('my-profile');
        } else {
            showDialog('Password Change Failed', 'Failed to change password.', 'error');
        }
    };

    const renderActiveComponent = () => {
        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                    <CircularProgress />
                </Box>
            );
        }

        // Special cases for edit-profile and change-password components
        if (activeComponent === 'edit-profile-temp') {
            return (
                <ProfileEdit
                    user={user}
                    onSave={handleSaveProfile}
                    onCancel={() => setActiveComponent('my-profile')}
                />
            );
        }

        if (activeComponent === 'change-password-temp') {
            return (
                <ChangePassword
                    onChangePassword={handleChangePasswordSubmit}
                    onCancel={() => setActiveComponent('my-profile')}
                />
            );
        }

        if (activeComponent === 'training-plans') {
            return (
                <TrainingPlans
                    userId="self"
                    role={user?.role || 'regular'}
                    userName={user?.email}
                    userFullName={user?.fullName}
                    affiliateId={user?.homeAffiliate}
                />
            );
        }

        if (activeComponent === 'cap-upload') {
            return <CapUploadPanel />;
        }

        // For all other components including my-profile
        const ActiveComponent = menuItems.find((item) => item.id === activeComponent)?.component || ProfileView;

        return (
            <ActiveComponent
                user={user}
                userId={user?.id}
                role={user?.role || 'regular'}
                userName={user?.email}
                userFullName={user?.fullName}
                affiliateId={user?.homeAffiliate}
                onUploadProfilePicture={handleUploadProfilePicture}
                onEditProfile={handleEditProfile}
                onChangePassword={handleChangePassword}
                isUploading={isUploading} // Edastame loading state'i
                showClassListPrivacySetting={activeComponent === 'my-profile'}
                isSavingClassListVisibility={isSavingClassListVisibility}
                onClassListVisibilityChange={handleClassListVisibilityChange}
            />
        );
    };

    // Horisontaalse menüü Tab kuvamiseks, lisame punase indikaatori vajadusel
    const renderTabLabel = (item) => {
        if (item.id === 'user-contracts' && sentCount > 0) {
            return (
                <Badge badgeContent={sentCount} color="error">
                    {item.label}
                </Badge>
            );
        }
        return item.label;
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                mt: 2,
                px: { xs: 0, sm: 4, md: 4, lg: 4 },
                display: 'flex',
                backgroundColor: 'background.default',
                pt: 2,
            }}
        >
            {/* Horisontaalne mobiilimenüü */}
            <Paper
                sx={{
                    position: 'absolute',
                    top: 51,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    display: { xs: 'block', md: 'none' },
                    overflow: 'hidden',
                }}
                elevation={1}
            >
                <Box
                    sx={{
                        overflowX: 'auto',
                        display: 'flex',
                        width: '100%',
                        WebkitOverflowScrolling: 'touch',
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: '20px',
                            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.9))',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Tabs
                        value={activeComponent === 'edit-profile-temp' || activeComponent === 'change-password-temp'
                            ? 'my-profile' : activeComponent}
                        onChange={(event, newValue) => setActiveComponent(newValue)}
                        variant="scrollable"
                        scrollButtons={false}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                            minHeight: '36px',
                            width: '100%',
                            '& .MuiTabs-flexContainer': {
                                width: '100%',
                                display: 'flex',
                                '& > *': {
                                    flexGrow: 1,
                                },
                            },
                            '& .MuiTab-root': {
                                minHeight: '36px',
                                py: 0.75,
                                textTransform: 'none',
                            },
                        }}
                    >
                        {menuItems.map((item) => (
                            <Tab
                                key={item.id}
                                label={renderTabLabel(item)}
                                value={item.id}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    minWidth: 'auto',
                                    fontWeight: (activeComponent === item.id ||
                                        (activeComponent === 'edit-profile-temp' && item.id === 'my-profile') ||
                                        (activeComponent === 'change-password-temp' && item.id === 'my-profile'))
                                        ? 'bold' : 'normal',
                                    fontSize: '0.85rem',
                                    px: 1,
                                    backgroundColor: (activeComponent === item.id ||
                                        (activeComponent === 'edit-profile-temp' && item.id === 'my-profile') ||
                                        (activeComponent === 'change-password-temp' && item.id === 'my-profile'))
                                        ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>
            </Paper>

            {/* Sidebar Navigation - desktop */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: 240,
                    flexShrink: 0,
                    position: 'relative !important',
                }}
                PaperProps={{
                    sx: {
                        position: 'relative',
                        borderRight: '1px solid #e0e0e0'
                    },
                }}
            >
                <Box sx={{ width: 220, p: 2 }}>
                    <List>
                        {menuItems.map((item) => (
                            <ListItem
                                button
                                key={item.id}
                                onClick={() => handleMenuClick(item.id)}
                                selected={activeComponent === item.id ||
                                    (activeComponent === 'edit-profile-temp' && item.id === 'my-profile') ||
                                    (activeComponent === 'change-password-temp' && item.id === 'my-profile')}
                                sx={{
                                    borderRadius: '4px',
                                    mb: 0.5,
                                    pl: 2,
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(33, 150, 243, 0.15)',
                                        borderLeft: '5px solid #2196f3',
                                        pl: 1.5,
                                        '&:hover': {
                                            backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            right: 0,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: 0,
                                            height: 0,
                                            borderTop: '8px solid transparent',
                                            borderBottom: '8px solid transparent',
                                            borderRight: '8px solid #fff',
                                        }
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        item.id === 'user-contracts' && sentCount > 0
                                            ? `${item.label} (${sentCount})`
                                            : item.label
                                    }
                                    primaryTypographyProps={{
                                        fontWeight: (activeComponent === item.id ||
                                            (activeComponent === 'edit-profile-temp' && item.id === 'my-profile') ||
                                            (activeComponent === 'change-password-temp' && item.id === 'my-profile'))
                                            ? '600' : 'normal',
                                        fontSize: '0.95rem',
                                        color: (activeComponent === item.id ||
                                            (activeComponent === 'edit-profile-temp' && item.id === 'my-profile') ||
                                            (activeComponent === 'change-password-temp' && item.id === 'my-profile'))
                                            ? '#1976d2' : 'rgba(0, 0, 0, 0.7)'
                                    }}
                                    sx={{
                                        color:
                                            item.id === 'user-contracts' && sentCount > 0
                                                ? 'red'
                                                : 'inherit',
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>

            {/* Sisu kuvamiseks peame lisama pikema marginaali, et jätta ruumi fikseeritud menüüle */}
            <Box
                sx={{
                    flexGrow: 1,
                    p: { xs: 0, sm: 2, md: 3, lg: 4 },
                    backgroundColor: 'background.default',
                    mt: { xs: 5, md: 0 },
                }}
            >
                <Card sx={{ backgroundColor: 'background.paper', p: 0, pt: 2, pb: 2 }}>
                    <CardContent>
                        {renderActiveComponent()}
                    </CardContent>
                </Card>
            </Box>

            {/* Snackbar teavituste jaoks */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Dialog teavituste jaoks */}
            <Dialog
                open={dialog.open}
                onClose={closeDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {dialog.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {dialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}